import React from 'react';
import { ModalController } from '../../../../../components/Modal/ControlledModal';
import { EventEdit, MetaSettings, updateEventDetails } from '../../../../../model/event';
import { useChanges, Form } from '../../../../../components/Form/Form';
import { TextField } from '../../../../../components/Form/TextField/TextField';
import { t } from '../../../../../locales/translate';
import { dig, getImageHash, readBytesUrl } from '../../../../../util/util';
import { isEmpty } from 'lodash';
import { handleError } from '../../../../../model/notification';
import { TicketLogoUploader } from '../TicketLogoUploader/TicketLogoUploader';
import { Logo } from '../../../../../model/logo';
import { Label } from '../../../../../components/Form/Label/Label';
import { FileUpload } from '../../../../../components/Form/FileUpload/FileUpload';
import { Stack } from '../../../../../components/Stack/Stack';
import { Spacer } from '../../../../../components/Spacer/Spacer';
import { Switcher } from '../../../../../components/Form/Switcher/Switcher';
import { Text } from '../../../../../components/text/text';

interface Props {
  controller: ModalController<EventEdit>;
  onUpdate?: () => void;
}

export function TicketForm({ controller, onUpdate }: Props): JSX.Element {
  const [changes, , model, trackChanges] = useChanges<
    EventEdit & { personalIdentifierDisclaimer: string | null; forcePrintPersonalization: boolean }
  >({
    ...controller.model,
    personalIdentifierDisclaimer: controller.model.meta.personalIdentifierDisclaimer ?? null,
    forcePrintPersonalization: controller.model.meta.forcePrintPersonalization ?? false,
  });

  async function handleOnSubmit(): Promise<void> {
    try {
      if (model.id) {
        const meta: MetaSettings = {
          ...model.meta,
          personalIdentifierDisclaimer: model.personalIdentifierDisclaimer,
        };
        // An unset key and `false` are equivalent, so only persist the flag when enabled.
        if (model.forcePrintPersonalization) {
          meta.forcePrintPersonalization = true;
        } else {
          delete meta.forcePrintPersonalization;
        }
        const changesToSubmit = {
          ...changes,
          logoIds: changes.logos?.map((item) => item.id),
          meta,
        };
        if (Object.keys(changesToSubmit).includes('ticketHeaderImage') && changesToSubmit['ticketHeaderImage']) {
          changesToSubmit['ticketHeaderImage'] = getImageHash(changesToSubmit['ticketHeaderImage']);
        }
        await updateEventDetails({
          eventId: model.id,
          changes: changesToSubmit,
        });
        controller.close();
        if (onUpdate) {
          onUpdate();
        }
      }
    } catch (error) {
      handleError(error);
    }
  }

  function handleOnLogosChange(logos: Logo[]): void {
    trackChanges(
      {
        logos,
      },
      'logos'
    );
  }

  return (
    <>
      <Form model={model} onInput={trackChanges}>
        {(form) => {
          controller.configure({
            onSubmit: handleOnSubmit,
            canSubmit: form.valid && !isEmpty(changes),
          });
          return (
            <Stack>
              {/* { Content } */}
              <TextField
                label={`${t.model.ticket.ticketline} 1`}
                name="artistOverride"
                placeholder={model.artist || ''}
              />
              <TextField label={`${t.model.ticket.ticketline} 2`} name="nameOverride" placeholder={model.name || ''} />
              <TextField
                label={`${t.model.ticket.ticketline} 3`}
                name="ticketSupportLineOverride"
                placeholder={model.ticketSupportLine}
              />
              <TextField
                label={`${t.model.ticket.ticketline} ${t.general.headline}`}
                name="ticketHeadlineOverride"
                placeholder={model.ticketHeadline}
              />
              <TextField
                label={`${t.model.ticket.ticketline} ${t.general.footer}`}
                name="additionalInformationOverride"
                placeholder={dig(model.additionalInformation, 'title')}
              />
              <TextField
                label={t.model.ticket.personalIdentifierDisclaimer}
                name="personalIdentifierDisclaimer"
                placeholder={model.meta?.personalIdentifierDisclaimer || undefined}
              />
              <Label title={t.model.ticket.forcePrintPersonalization} />
              <Stack size="xxs">
                <Switcher
                  value={model.forcePrintPersonalization}
                  labels={[t.general.yes, t.general.no]}
                  onChange={(value) => trackChanges({ forcePrintPersonalization: value }, 'forcePrintPersonalization')}
                />
                <Text type="form-description">{t.model.ticket.forcePrintPersonalizationDescription}</Text>
              </Stack>
            </Stack>
          );
        }}
      </Form>
      <Spacer size="xl" />
      <Stack>
        <Label title="Logos" />
        <TicketLogoUploader onChange={handleOnLogosChange} logos={model.logos || []} />
        <FileUpload
          label={t.model.ticket.headerImage}
          image={model.ticketHeaderImage}
          onChange={async (files) => {
            trackChanges(
              {
                ticketHeaderImage: files.length > 0 ? await readBytesUrl(files[0], true) : null,
              },
              'ticketHeaderImage'
            );
          }}
        />
      </Stack>
    </>
  );
}
